'use client';

import { UserButton, useUser } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';

import { isMaintainerUser } from '../../libs/auth/maintainer-identity';
import { LanguageIcon, LanguagePanel } from './LanguageControls';
import { OrgPlanPanel, PersonalPlanPanel, PlanIcon } from './PlanControls';

/**
 * The account avatar/menu. Every signed-in user gets a "Language" page (avatar →
 * Manage account); the maintainer additionally gets a custom "Dev" page with the
 * personal- and active-org plan toggles (the active-org one kept aligned to the URL
 * workspace by `OrgActiveSync`). Integrations and API keys moved to the in-app
 * workspace Settings page (`/[org]/settings`).
 */
export function AccountMenu(): React.JSX.Element {
  const { user } = useUser();
  const isOwner = user ? isMaintainerUser(user) : false;
  const t = useTranslations('home');

  return (
    <UserButton>
      {/* Adding a custom action means re-declaring the built-in items. */}
      <UserButton.MenuItems>
        <UserButton.Action
          label={t('languageLabel')}
          open="language"
          labelIcon={<LanguageIcon />}
        />
        <UserButton.Action label="manageAccount" />
        <UserButton.Action label="signOut" />
      </UserButton.MenuItems>
      <UserButton.UserProfilePage
        label={t('languageLabel')}
        url="language"
        labelIcon={<LanguageIcon />}
      >
        <LanguagePanel />
      </UserButton.UserProfilePage>
      {/* Maintainer-only dev tooling: personal + active-org plan toggles, one page. */}
      {isOwner ? (
        <UserButton.UserProfilePage
          label="Dev"
          url="dev"
          labelIcon={<PlanIcon />}
        >
          <div className="space-y-8">
            <PersonalPlanPanel />
            <OrgPlanPanel />
          </div>
        </UserButton.UserProfilePage>
      ) : null}
    </UserButton>
  );
}
